import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { getAuthHeaders, useAuth } from '../contexts/AuthContext';
import { useCountry } from '../contexts/CountryContext';
import { canEditModule, viewOnlyHint } from '../lib/permissions';
import { StatusBadge } from '../components/StatusBadge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { CancelDraftButton } from '../components/CancelDraftButton';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { LoadError, errorMessage } from '../components/LoadError';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { platformLabel } from '../config/platforms';
import { Term, PLANNER_TERMS } from '../components/Term';
import {
  ArrowLeft, Calculator, CheckCircle2, AlertTriangle, Boxes, Package,
  Container, IndianRupee, Gauge, RefreshCw, MapPin, Layers, TrendingUp, Lock
} from 'lucide-react';

import { useChannels } from '../hooks/useChannels';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const REGION_COLORS = {
  Bengaluru: '#5856D6', Mumbai: '#FF9500', Hyderabad: '#AF52DE',
  Gurgaon: '#007AFF', Chennai: '#34C759',
};

// Text uses 700 shades (WCAG AA on white); bars keep the brighter accent colours.
const fillTone = (pct) => {
  if (pct > 100) return { text: 'text-red-700', bar: 'bg-[#FF3B30]', accent: '#B91C1C' };
  if (pct >= 70) return { text: 'text-green-700', bar: 'bg-[#34C759]', accent: '#15803D' };
  return { text: 'text-amber-700', bar: 'bg-[#FF9500]', accent: '#B45309' };
};

/** Raw "days of cover" text -> integer 1..365, or an error message. */
const parseDaysOfCover = (raw) => {
  if (String(raw).trim() === '') return { error: 'Enter days of cover (1–365).' };
  const n = Number(raw);
  if (!Number.isInteger(n) || n < 1 || n > 365) return { error: 'Use a whole number of days from 1 to 365.' };
  return { value: n };
};

const KPICard = ({ icon: Icon, label, value, sub, accent = '#111111', testId, delay = 0, children }) => (
  <div
    className="sp-card sp-card-lift sp-rise rounded-xl p-4"
    style={{ animationDelay: `${delay}ms` }}
    data-testid={testId}
  >
    <div className="flex items-center justify-between mb-2">
      <span className="sp-body text-[10px] font-semibold uppercase tracking-[0.12em] text-gray-500">{label}</span>
      <Icon className="h-3.5 w-3.5 text-gray-400" strokeWidth={2} aria-hidden="true" />
    </div>
    <div className="sp-num text-[26px] font-bold leading-none" style={{ color: accent }}>{value}</div>
    {sub && <div className="sp-body text-[11px] text-gray-500 mt-1.5">{sub}</div>}
    {children}
  </div>
);

export default function POSplitPlannerPage() {
  useChannels();   // re-render when channel names or colours change (Settings → Channels)
  const { poNumber } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedPlanId = searchParams.get('planId');
  const { country } = useCountry();
  const { user } = useAuth();
  const canEdit = canEditModule(user, 'shipment_planning');

  const [po, setPo] = useState(null);
  const [plan, setPlan] = useState(null);
  const [lines, setLines] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [containers, setContainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [approving, setApproving] = useState(false);
  const [editQty, setEditQty] = useState({});
  const [loadError, setLoadError] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const [confirmApproveOpen, setConfirmApproveOpen] = useState(false);

  // Raw text so the field can be cleared while typing; validated before generating.
  const [daysOfCoverRaw, setDaysOfCoverRaw] = useState('30');
  const daysCheck = parseDaysOfCover(daysOfCoverRaw);
  const daysOfCover = daysCheck.value;
  const [containerType, setContainerType] = useState('32FT');
  const [roundToCarton, setRoundToCarton] = useState(true);

  const fetchPlanDetails = useCallback(async (planId) => {
    const res = await axios.get(`${API}/shipment-planning/allocation-plans/${planId}`, { headers: getAuthHeaders() });
    setPlan(res.data.plan);
    setLines(res.data.lines);
    setDaysOfCoverRaw(String(res.data.plan.target_days_of_cover || 30));
    setContainerType(res.data.plan.container_type || '32FT');
  }, []);

  const load = useCallback(async () => {
    try {
      const [posRes, contRes, plansRes] = await Promise.all([
        axios.get(`${API}/retail-pos?country=${country}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/shipment-planning/container-types`, { headers: getAuthHeaders() }),
        axios.get(`${API}/shipment-planning/allocation-plans`, { headers: getAuthHeaders(), params: { po_number: poNumber, country } })
      ]);
      const found = posRes.data.find(p => p.po_number === poNumber);
      setPo(found || null);
      setContainers(contRes.data);
      const existing = plansRes.data.filter(p => p.po_number === poNumber);
      if (requestedPlanId || existing.length > 0) {
        await fetchPlanDetails(requestedPlanId || existing[0].id);
      }
      setLoadError(null);
    } catch (e) {
      setLoadError(e);
    } finally {
      setLoading(false);
      setRetrying(false);
    }
  }, [poNumber, country, fetchPlanDetails, requestedPlanId]);

  useEffect(() => { load(); }, [load]);

  const retryLoad = () => {
    setRetrying(true);
    load();
  };

  const generatePlan = async () => {
    if (!daysOfCover) return;
    setGenerating(true);
    try {
      let res;
      if (plan && plan.planning_status === 'DRAFT') {
        res = await axios.post(
          `${API}/shipment-planning/allocation-plans/${plan.id}/recalculate`,
          { allocation_plan_id: plan.id, target_days_of_cover: daysOfCover, container_type: containerType, round_to_carton: roundToCarton },
          { headers: getAuthHeaders() }
        );
      } else {
        res = await axios.post(
          `${API}/shipment-planning/allocate-po`,
          { po_id: po.id, po_number: poNumber, target_days_of_cover: daysOfCover, container_type: containerType, round_to_carton: roundToCarton },
          { headers: getAuthHeaders() }
        );
      }
      setWarnings(res.data.warnings || []);
      await fetchPlanDetails(res.data.allocation_plan_id);
      setEditQty({});
      toast.success('Allocation plan generated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate plan');
    } finally {
      setGenerating(false);
    }
  };

  const saveOverride = async (line) => {
    const val = parseInt(editQty[line.id]);
    if (isNaN(val) || val < 0) return;
    try {
      await axios.put(
        `${API}/shipment-planning/allocation-lines/${line.id}/override`,
        { allocation_line_id: line.id, manual_override_qty: val },
        { headers: getAuthHeaders() }
      );
      await fetchPlanDetails(plan.id);
      setEditQty(prev => { const n = { ...prev }; delete n[line.id]; return n; });
      toast.success(`${line.sku_code} → ${line.destination_fc_code} updated to ${val}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save override');
    }
  };

  // Called from the confirm dialog; rethrows so the dialog stays open on failure.
  const approvePlan = async () => {
    setApproving(true);
    try {
      const res = await axios.post(
        `${API}/shipment-planning/allocation-plans/${plan.id}/approve`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success(`Approved! ${res.data.shipment_ids.length} FC shipments created`);
      navigate('/shipment-planning?tab=shipments');
    } catch (e) {
      toast.error(errorMessage(e, 'Failed to approve plan'));
      throw e;
    } finally {
      setApproving(false);
    }
  };

  const fcGroups = useMemo(() => {
    const groups = {};
    lines.forEach(l => {
      const key = l.destination_fc_code;
      if (!groups[key]) groups[key] = { fc: l, lines: [], units: 0, cartons: 0, cbm: 0, value: 0 };
      groups[key].lines.push(l);
      groups[key].units += l.final_qty || 0;
      groups[key].cartons += l.cartons || 0;
      groups[key].cbm += l.cbm || 0;
      groups[key].value += l.shipment_value || 0;
    });
    return Object.values(groups).sort((a, b) => b.units - a.units);
  }, [lines]);

  const maxGroupUnits = fcGroups.length ? Math.max(...fcGroups.map(g => g.units)) : 1;

  // A Final Qty typed but not yet saved (value differs from the line's current final_qty).
  const hasUnsavedEdits = useMemo(() => Object.entries(editQty).some(([id, val]) => {
    const line = lines.find(l => String(l.id) === id);
    return line && parseInt(val) !== line.final_qty;
  }), [editQty, lines]);

  if (loading) {
    return <div role="status" className="text-center py-16 text-gray-500 sp-body text-[13px]">Loading planner…</div>;
  }

  if (loadError) {
    return (
      <div className="py-16 max-w-xl mx-auto space-y-4" data-testid="po-load-error">
        <LoadError what={`PO ${poNumber}`} error={loadError} onRetry={retryLoad} retrying={retrying} testId="po-split-load-error" />
        <Button variant="outline" onClick={() => navigate('/retail-pos')} className="h-9 text-[13px]">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Retail POs
        </Button>
      </div>
    );
  }

  if (!po) {
    return (
      <div className="text-center py-16" data-testid="po-not-found">
        <p className="text-gray-500 sp-body text-[14px]">PO {poNumber} not found</p>
        <Button variant="outline" onClick={() => navigate('/retail-pos')} className="mt-4 h-9 text-[13px]">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Retail POs
        </Button>
      </div>
    );
  }

  const isDraft = plan?.planning_status === 'DRAFT';
  const isApproved = plan?.planning_status === 'APPROVED';
  const isApproving = plan?.planning_status === 'APPROVING';
  const isCancelled = plan?.planning_status === 'CANCELLED';
  // Settings and the generate button are locked once approval starts, or for view-only roles.
  const settingsLocked = isApproved || isApproving || !canEdit;
  const editableLines = isDraft && canEdit;
  const tone = fillTone(plan?.container_fill_percentage || 0);
  const fillPct = plan?.container_fill_percentage;
  const totalUnits = fcGroups.reduce((s, g) => s + g.units, 0);
  const totalValue = plan?.total_suggested_value ?? fcGroups.reduce((s, g) => s + g.value, 0);

  return (
    <div className="space-y-5 sp-body pb-24" data-testid="po-split-planner-page">
      {/* Header */}
      <div className="sp-rise flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/retail-pos')} className="h-9 w-9 shrink-0 rounded-lg mt-1" aria-label="Back to Retail POs" data-testid="back-to-pos-btn">
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          </Button>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">PO Split Planner</h1>
              {plan && <StatusBadge status={plan.planning_status} data-testid="plan-status-badge" />}
              {!canEdit && (
                <span className="inline-flex items-center gap-1 text-[12px] font-medium text-gray-600" title={viewOnlyHint(user)} data-testid="split-view-only">
                  <Lock className="h-3.5 w-3.5" aria-hidden="true" /> View only
                </span>
              )}
            </div>
            <p className="mt-1 text-[13px] text-gray-500 flex flex-wrap items-center gap-x-2">
              <span className="sp-num font-semibold text-gray-800">{poNumber}</span>
              <span className="text-gray-400" aria-hidden="true">/</span>
              <span>{platformLabel(po.platform)}</span>
              <span className="text-gray-400" aria-hidden="true">/</span>
              <span>{po.po_date}</span>
            </p>
          </div>
        </div>
        {canEdit && isDraft && <CancelDraftButton planId={plan.id} onCancelled={() => fetchPlanDetails(plan.id)} disabled={generating || approving} />}
        {canEdit && isApproving && <Button onClick={() => setConfirmApproveOpen(true)} disabled={approving} data-testid="resume-split-approval">{approving ? 'Approving…' : 'Resume approval'}</Button>}
      </div>

      {isCancelled && (
        <div role="status" className="sp-rise rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-[13px] text-gray-700" data-testid="plan-cancelled-notice">
          This plan was cancelled, so its stock and PO quantities were released.{canEdit ? ' Generate a new plan below to plan this PO again.' : ''}
        </div>
      )}

      {/* Sticky Glass Settings Bar */}
      <div className="sp-glass sp-rise sticky top-0 z-20 rounded-xl border border-black/5 shadow-sm px-5 py-3.5" style={{ animationDelay: '60ms' }} data-testid="planner-settings">
        <div className="flex flex-wrap items-end gap-x-6 gap-y-3">
          <div className="space-y-1">
            <Label htmlFor="days-of-cover-input" className="text-[10px] font-semibold uppercase tracking-[0.1em] text-gray-500">Days of Cover</Label>
            <Input
              id="days-of-cover-input"
              type="number" min={1} max={365} step={1} inputMode="numeric"
              value={daysOfCoverRaw}
              onChange={(e) => setDaysOfCoverRaw(e.target.value)}
              aria-invalid={!!daysCheck.error}
              aria-describedby={daysCheck.error ? 'days-of-cover-error' : undefined}
              className={`h-9 w-28 text-[13px] sp-num font-semibold bg-white ${daysCheck.error ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
              data-testid="days-of-cover-input"
              disabled={settingsLocked}
            />
            {daysCheck.error && !settingsLocked && (
              <p id="days-of-cover-error" className="text-[11px] text-red-700 max-w-[220px]" data-testid="days-of-cover-error">{daysCheck.error}</p>
            )}
          </div>
          <div className="space-y-1">
            <Label htmlFor="container-type-select" className="text-[10px] font-semibold uppercase tracking-[0.1em] text-gray-500">Container</Label>
            <Select value={containerType} onValueChange={setContainerType} disabled={settingsLocked}>
              <SelectTrigger id="container-type-select" className="h-9 w-40 text-[13px] bg-white" data-testid="container-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {containers.map(c => (
                  <SelectItem key={c.container_type} value={c.container_type} className="text-[13px]">
                    {c.container_type} · {c.max_cbm} CBM
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2 pb-2">
            <Switch id="round-to-carton-switch" checked={roundToCarton} onCheckedChange={setRoundToCarton} disabled={settingsLocked} data-testid="round-to-carton-switch" />
            <Label htmlFor="round-to-carton-switch" className="text-[12px] font-medium text-gray-600">Round to carton</Label>
          </div>
          <div className="flex-1" />
          {!settingsLocked && (
            <Button
              onClick={generatePlan}
              disabled={generating || !daysOfCover}
              className="h-9 px-5 bg-[#007AFF] hover:bg-[#005bb5] active:scale-95 text-white text-[13px] font-semibold rounded-lg shadow-sm transition-transform"
              data-testid="generate-plan-btn"
            >
              {generating ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Calculator className="h-4 w-4 mr-2" />}
              {isDraft ? 'Recalculate' : isCancelled ? 'Generate New Plan' : 'Generate Split Plan'}
            </Button>
          )}
        </div>
      </div>

      {/* Warnings */}
      {warnings.length > 0 && (
        <div className="sp-rise rounded-xl border border-[#FFCC00]/40 bg-[#FFFBEB] px-4 py-3" data-testid="plan-warnings">
          <div className="flex items-start gap-2.5">
            <AlertTriangle className="h-4 w-4 text-[#B45309] mt-0.5 flex-shrink-0" />
            <div className="space-y-0.5 flex-1">
              <span className="text-[11px] font-bold uppercase tracking-wider text-[#B45309]">{warnings.length} Warning{warnings.length > 1 ? 's' : ''}</span>
              {warnings.map((w) => (
                <p key={w} className="text-[12px] text-[#92400E] leading-relaxed">{w}</p>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* KPI Grid */}
      {plan && (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4" data-testid="plan-kpi-cards">
          <KPICard icon={Package} label="PO Units" value={plan.total_po_units?.toLocaleString('en-IN')} sub="Plannable quantity" testId="kpi-po-units" delay={0} />
          <KPICard icon={Boxes} label="Suggested" value={plan.total_suggested_units?.toLocaleString('en-IN')} sub={`${((plan.total_suggested_units / (plan.total_po_units || 1)) * 100).toFixed(0)}% of PO covered`} accent="#007AFF" testId="kpi-suggested-units" delay={40} />
          <KPICard icon={Layers} label="Cartons" value={plan.total_cartons?.toLocaleString('en-IN')} sub="Across all FCs" testId="kpi-cartons" delay={80} />
          <KPICard icon={Container} label="Total volume" value={plan.total_cbm != null ? `${plan.total_cbm.toFixed(2)} m³` : '—'} sub={`CBM · ${plan.container_type} container`} testId="kpi-cbm" delay={120} />
          <KPICard icon={Gauge} label="Container Fill" value={`${plan.container_fill_percentage?.toFixed(1)}%`} accent={tone.accent} testId="kpi-fill" delay={160}>
            <div className="mt-2 h-1.5 w-full rounded-full bg-gray-100 overflow-hidden">
              <div className={`h-full rounded-full ${tone.bar} transition-all duration-700`} style={{ width: `${Math.min(plan.container_fill_percentage || 0, 100)}%` }} />
            </div>
          </KPICard>
          <KPICard icon={IndianRupee} label="Ship Value" value={`₹${((plan.total_suggested_value || 0) / 100000).toFixed(1)}L`} sub={`₹${(plan.total_suggested_value || 0).toLocaleString('en-IN')}`} testId="kpi-value" delay={200} />
        </div>
      )}

      {/* FC allocation groups */}
      {fcGroups.length > 0 ? (
        <div className="space-y-4" data-testid="fc-allocation-groups">
          {fcGroups.map((group, gi) => {
            const regionColor = REGION_COLORS[group.fc.destination_region] || '#8E8E93';
            return (
              <div
                key={group.fc.destination_fc_code}
                className="sp-card sp-rise rounded-xl overflow-hidden shadow-sm"
                style={{ animationDelay: `${120 + gi * 50}ms` }}
                data-testid={`fc-group-${group.fc.destination_fc_code}`}
              >
                {/* FC header */}
                <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 border-b border-black/5" style={{ background: `linear-gradient(90deg, ${regionColor}0D 0%, transparent 45%)` }}>
                  <div className="flex items-center gap-3">
                    <span className="sp-num px-2.5 py-1 rounded-lg text-[12px] font-bold text-white shadow-sm" style={{ background: regionColor }}>
                      {group.fc.destination_fc_code}
                    </span>
                    <div>
                      <div className="text-[13px] font-semibold text-gray-800">{group.fc.destination_fc_name}</div>
                      <div className="flex items-center gap-1 text-[11px] text-gray-500">
                        <MapPin className="h-3 w-3" aria-hidden="true" /> {group.fc.destination_region}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                    {/* mini volume bar */}
                    <div className="hidden lg:flex items-center gap-2">
                      <div className="h-1.5 w-28 rounded-full bg-gray-100 overflow-hidden">
                        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${(group.units / maxGroupUnits) * 100}%`, background: regionColor }} />
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-y-1 divide-x divide-black/5 text-[12px]">
                      <div className="pr-4 text-right">
                        <div className="sp-num font-bold text-gray-900">{group.units}</div>
                        <div className="text-[10px] uppercase tracking-wider text-gray-500">Units</div>
                      </div>
                      <div className="px-4 text-right">
                        <div className="sp-num font-bold text-gray-900">{group.cartons}</div>
                        <div className="text-[10px] uppercase tracking-wider text-gray-500">Cartons</div>
                      </div>
                      <div className="px-4 text-right">
                        <div className="sp-num font-bold text-gray-900">{group.cbm.toFixed(2)}</div>
                        <div className="text-[10px] uppercase tracking-wider text-gray-500">CBM</div>
                      </div>
                      <div className="pl-4 text-right">
                        <div className="sp-num font-bold text-gray-900">₹{group.value.toLocaleString('en-IN')}</div>
                        <div className="text-[10px] uppercase tracking-wider text-gray-500">Value</div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* SKU lines */}
                <div className="overflow-x-auto">
                  <table className="w-full text-[12px] sp-grid-table">
                    <thead className="sp-glass sticky top-0">
                      <tr className="text-gray-500">
                        <th className="text-left px-5 py-2 text-[10px] font-bold uppercase tracking-[0.1em]">SKU</th>
                        <th className="text-right px-3 py-2 text-[10px] font-bold uppercase tracking-[0.1em]"><Term hint={PLANNER_TERMS.share}>Share %</Term></th>
                        <th className="text-right px-3 py-2 text-[10px] font-bold uppercase tracking-[0.1em]"><Term hint={PLANNER_TERMS.wtAds}>Wt. ADS</Term></th>
                        <th className="text-right px-3 py-2 text-[10px] font-bold uppercase tracking-[0.1em]"><Term hint={PLANNER_TERMS.fcStock}>FC Stock</Term></th>
                        <th className="text-right px-3 py-2 text-[10px] font-bold uppercase tracking-[0.1em]"><Term hint={PLANNER_TERMS.gap}>Gap</Term></th>
                        <th className="text-right px-3 py-2 text-[10px] font-bold uppercase tracking-[0.1em]">Suggested</th>
                        <th className="text-center px-3 py-2 text-[10px] font-bold uppercase tracking-[0.1em] w-32">Final Qty</th>
                        <th className="text-right px-3 py-2 text-[10px] font-bold uppercase tracking-[0.1em]"><Term hint="Cartons">Ctns</Term></th>
                        <th className="text-right px-3 py-2 text-[10px] font-bold uppercase tracking-[0.1em]"><Term hint={PLANNER_TERMS.cbm}>CBM</Term></th>
                        <th className="text-left px-5 py-2 text-[10px] font-bold uppercase tracking-[0.1em]">Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.lines.map((line) => (
                        <tr key={line.id} data-testid={`alloc-line-${line.sku_code}-${line.destination_fc_code}`}>
                          <td className="px-5 py-2.5">
                            <div className="sp-num font-semibold text-gray-800">{line.sku_code}</div>
                            <div className="text-[11px] text-gray-500 truncate max-w-[170px]">{line.product_name}</div>
                          </td>
                          <td className="px-3 py-2.5 text-right sp-num text-gray-600">{line.sales_share_percentage?.toFixed(1)}%</td>
                          <td className="px-3 py-2.5 text-right sp-num text-gray-600">{line.weighted_ads?.toFixed(1)}</td>
                          <td className="px-3 py-2.5 text-right sp-num text-gray-600">{line.current_fc_stock}</td>
                          <td className="px-3 py-2.5 text-right sp-num text-gray-600">{line.demand_gap_qty}</td>
                          <td className="px-3 py-2.5 text-right sp-num text-gray-600">{line.suggested_qty_final}</td>
                          <td className="px-3 py-2.5 text-center bg-blue-50/30">
                            {editableLines ? (
                              <div className="flex items-center justify-center gap-1">
                                <Input
                                  type="number"
                                  min={0}
                                  aria-label={`Final quantity for ${line.sku_code} to ${line.destination_fc_code}`}
                                  value={editQty[line.id] !== undefined ? editQty[line.id] : line.final_qty}
                                  onChange={(e) => { const v = e.target.value; setEditQty(prev => ({ ...prev, [line.id]: v })); }}
                                  className={`h-7 w-20 text-center text-[12px] sp-num font-bold bg-white ${line.manual_override_qty != null ? 'border-[#AF52DE] bg-purple-50 text-purple-800' : ''}`}
                                  data-testid={`final-qty-input-${line.sku_code}-${line.destination_fc_code}`}
                                />
                                {editQty[line.id] !== undefined && parseInt(editQty[line.id]) !== line.final_qty && (
                                  <Button size="sm" onClick={() => saveOverride(line)} disabled={!/^\d+$/.test(String(editQty[line.id]).trim())} title={/^\d+$/.test(String(editQty[line.id]).trim()) ? undefined : 'Enter a whole number of 0 or more'} className="h-7 px-2 text-[10px] font-bold bg-[#AF52DE] hover:bg-[#9542BD] active:scale-95" data-testid={`save-override-${line.sku_code}-${line.destination_fc_code}`}>
                                    Save
                                  </Button>
                                )}
                              </div>
                            ) : (
                              <span className="sp-num font-bold text-gray-900">{line.final_qty}</span>
                            )}
                          </td>
                          <td className="px-3 py-2.5 text-right sp-num text-gray-600">{line.cartons}</td>
                          <td className="px-3 py-2.5 text-right sp-num text-gray-600">{line.cbm?.toFixed(2)}</td>
                          <td className="px-5 py-2.5 text-gray-600 text-[11px] max-w-[210px] leading-snug">{line.reason_for_suggestion}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="sp-card sp-rise rounded-xl p-14 text-center" data-testid="no-plan-placeholder">
          <div className="inline-flex items-center justify-center h-14 w-14 rounded-2xl bg-blue-50 mb-4">
            <Calculator className="h-6 w-6 text-[#007AFF]" strokeWidth={1.75} aria-hidden="true" />
          </div>
          <p className="sp-heading text-[16px] font-bold text-gray-800">No allocation plan yet</p>
          <p className="text-[12px] text-gray-500 mt-1.5 max-w-sm mx-auto">
            {canEdit
              ? 'Set your target days of cover and container type above, then generate a rule-based split of this PO across your Amazon FCs.'
              : 'No split plan has been generated for this PO yet.'}
          </p>
        </div>
      )}

      {/* Sticky approve action bar */}
      {editableLines && lines.length > 0 && (
        <div className="sticky bottom-4 z-30">
          <div className="sp-glass rounded-xl border border-black/10 shadow-lg px-4 sm:px-5 py-3 flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-[12px] text-gray-600">
              <span className="flex items-center gap-1.5"><TrendingUp className="h-3.5 w-3.5 text-[#007AFF]" /><span className="sp-num font-bold">{plan.total_suggested_units}</span> units</span>
              <span><span className="sp-num font-bold">{fcGroups.length}</span> FCs</span>
              <span><span className="sp-num font-bold">{plan.total_cartons}</span> cartons</span>
              <span className={`sp-num font-bold ${tone.text}`}>{plan.container_fill_percentage?.toFixed(1)}% fill</span>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              {hasUnsavedEdits && (
                <span className="flex flex-wrap items-center gap-2 text-[12px] text-amber-800" data-testid="unsaved-qty-hint">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  Save or discard your quantity changes first
                  <Button variant="ghost" size="sm" onClick={() => setEditQty({})} className="h-7 px-2 text-[11px] font-semibold" data-testid="discard-qty-edits-btn">
                    Discard
                  </Button>
                </span>
              )}
              <Button
                onClick={() => setConfirmApproveOpen(true)}
                disabled={approving || hasUnsavedEdits}
                className="h-10 px-6 bg-[#34C759] hover:bg-[#2DA84E] active:scale-95 text-white text-[13px] font-bold rounded-lg shadow-sm transition-transform"
                data-testid="approve-plan-btn"
              >
                <CheckCircle2 className="h-4 w-4 mr-2" />
                {approving ? 'Approving…' : 'Approve & Create Shipments'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {plan && canEdit && (
        <ConfirmDialog
          open={confirmApproveOpen}
          onOpenChange={setConfirmApproveOpen}
          title={`Approve plan for PO ${poNumber}?`}
          description="Approval reserves warehouse stock and creates one shipment per FC. Check the summary below before continuing."
          confirmLabel={`Approve & create ${fcGroups.length} shipment${fcGroups.length === 1 ? '' : 's'}`}
          busyLabel="Approving…"
          onConfirm={approvePlan}
          testId="approve-split-dialog"
        >
          <div className="space-y-3 text-[13px]" data-testid="approve-split-summary">
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 rounded-lg border border-black/5 bg-gray-50 p-3">
              <dt className="text-gray-500">Total units</dt>
              <dd className="sp-num text-right font-semibold text-gray-900">{totalUnits.toLocaleString('en-IN')}</dd>
              <dt className="text-gray-500">FC shipments</dt>
              <dd className="sp-num text-right font-semibold text-gray-900">{fcGroups.length}</dd>
              <dt className="text-gray-500">Cartons</dt>
              <dd className="sp-num text-right font-semibold text-gray-900">{(plan.total_cartons || 0).toLocaleString('en-IN')}</dd>
              <dt className="text-gray-500">Value</dt>
              <dd className="sp-num text-right font-semibold text-gray-900">₹{Math.round(totalValue || 0).toLocaleString('en-IN')}</dd>
              {fillPct != null && (
                <>
                  <dt className="text-gray-500">Container fill</dt>
                  <dd className={`sp-num text-right font-semibold ${tone.text}`}>{fillPct.toFixed(1)}% · {plan.container_type}</dd>
                </>
              )}
            </dl>
            <p className="text-gray-600">
              <span className="font-medium text-gray-800">FCs: </span>
              {fcGroups.map(g => `${g.fc.destination_fc_code} (${g.units.toLocaleString('en-IN')})`).join(', ')}
            </p>
            {fillPct != null && (fillPct < 70 || fillPct > 100) && (
              <p className="flex items-start gap-2 rounded-lg border border-[#FFCC00]/40 bg-[#FFFBEB] p-2.5 text-[12px] text-[#92400E]" data-testid="approve-split-fill-warning">
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0 text-[#B45309]" />
                {fillPct > 100
                  ? `Container fill is ${fillPct.toFixed(1)}%, over the capacity of a ${plan.container_type} container.`
                  : `Container is only ${fillPct.toFixed(1)}% full (under 70%). Consider adding units before shipping.`}
              </p>
            )}
          </div>
        </ConfirmDialog>
      )}
    </div>
  );
}
